import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Badge from "@material-ui/core/Badge";
import Chat from "@material-ui/icons/Chat";
import DoneAll from "@material-ui/icons/DoneAll";
import FilterListIcon from "@material-ui/icons/FilterList";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import TicketsQueueSelect from "../TicketsQueueSelect";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField
} from "@material-ui/core";
import api from "../../services/api";
import usePaginatedUsers from "../../hooks/usePaginatedUsers";
import useWhatsApps from "../../hooks/useWhatsApps";

const useStyles = makeStyles((theme) => ({
  colorIcon: {
    color: "#0035a2"
  },
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary
  },
  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.background.paper
  },
  tab: {
    minWidth: 120,
    width: 120
  },
  openTab: {
    minWidth: 0,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  openTabLabel: {
    whiteSpace: "nowrap",
    fontSize: "0.82rem"
  },
  openTabBadge: {
    display: "inline-flex",
    alignItems: "center",
    paddingRight: theme.spacing(2)
  },
  ticketOptionsBox: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "space-between",
    alignItems: "center",
    background: theme.palette.background.paper,
    padding: theme.spacing(1),
    flexWrap: "wrap"
  },
  optionButton: {
    flex: "none"
  },
  serachInputWrapper: {
    flex: "1 1 220px",
    background: theme.palette.background.default,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1)
  },
  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center"
  },
  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default
  },
  filterControl: {
    minWidth: 180
  },
  filterChips: {
    display: "flex",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    padding: theme.spacing(0, 1, 1),
    background: theme.palette.background.paper
  }
}));

const normalizeIds = values => (values || []).map(Number).sort().join(",");

const TicketsManager = () => {
  const classes = useStyles();
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [tabOpen, setTabOpen] = useState("mine");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const [mineCount, setMineCount] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const [tags, setTags] = useState([]);
  const {
    users,
    loading: loadingUsers,
    hasMore: usersHasMore,
    menuProps: usersMenuProps
  } = usePaginatedUsers();
  const { loadingWhatsapps, whatsApps } = useWhatsApps();

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTagId, setSelectedTagId] = useState("");
  const [selectedAssignedUserId, setSelectedAssignedUserId] = useState("");
  const [selectedDateFrom, setSelectedDateFrom] = useState("");
  const [selectedDateTo, setSelectedDateTo] = useState("");
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [selectedTransferred, setSelectedTransferred] = useState("");
  const [selectedUnreadOnly, setSelectedUnreadOnly] = useState(false);

  const [draftQueueIds, setDraftQueueIds] = useState(userQueueIds || []);
  const [draftTagId, setDraftTagId] = useState("");
  const [draftAssignedUserId, setDraftAssignedUserId] = useState("");
  const [draftDateFrom, setDraftDateFrom] = useState("");
  const [draftDateTo, setDraftDateTo] = useState("");
  const [draftWhatsappId, setDraftWhatsappId] = useState("");
  const [draftTransferred, setDraftTransferred] = useState("");
  const [draftUnreadOnly, setDraftUnreadOnly] = useState(false);

  const usersFromSelectedQueues = users.filter((userItem) => {
    if (!selectedQueueIds.length) return true;
    if (!Array.isArray(userItem.queues)) return false;

    return userItem.queues.some((queue) => selectedQueueIds.includes(queue.id));
  });

  const usersFromDraftQueues = users.filter((userItem) => {
    if (!draftQueueIds.length) return true;
    if (!Array.isArray(userItem.queues)) return false;

    return userItem.queues.some((queue) => draftQueueIds.includes(queue.id));
  });

  const activeFilterCount = [
    normalizeIds(selectedQueueIds) !== normalizeIds(userQueueIds),
    Boolean(selectedTagId),
    Boolean(selectedAssignedUserId),
    Boolean(selectedDateFrom),
    Boolean(selectedDateTo),
    Boolean(selectedWhatsappId),
    Boolean(selectedTransferred),
    Boolean(selectedUnreadOnly)
  ].filter(Boolean).length;

  const selectedTag = tags.find(tag => String(tag.id) === String(selectedTagId));
  const selectedAssignedUser = users.find(
    userItem => String(userItem.id) === String(selectedAssignedUserId)
  );
  const selectedWhatsapp = whatsApps.find(
    whatsapp => String(whatsapp.id) === String(selectedWhatsappId)
  );
  const queueFilterActive =
    normalizeIds(selectedQueueIds) !== normalizeIds(userQueueIds);
  const canShowAllTickets =
    user?.permissions?.includes("tickets:show-all") ||
    user?.permissions?.includes("tickets-manager:showall");

  useEffect(() => {
    if (canShowAllTickets) {
      setShowAllTickets(true);
    }
  }, [canShowAllTickets]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
      setSearchParam("");
    }
  }, [tab]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const { data } = await api.get("/tags");
        setTags(Array.isArray(data) ? data : []);
      } catch (_error) {
        setTags([]);
      }
    };

    loadTags();
  }, []);

  useEffect(() => {
    if (!selectedAssignedUserId || selectedAssignedUserId === "unassigned") {
      return;
    }
    if (usersHasMore || loadingUsers) return;

    const stillAvailable = usersFromSelectedQueues.some(
      (userItem) => String(userItem.id) === String(selectedAssignedUserId)
    );

    if (!stillAvailable) {
      setSelectedAssignedUserId("");
    }
  }, [selectedQueueIds, selectedAssignedUserId, usersFromSelectedQueues, usersHasMore, loadingUsers]);

  useEffect(() => {
    if (!filtersModalOpen) return;
    if (!draftAssignedUserId || draftAssignedUserId === "unassigned") {
      return;
    }
    if (usersHasMore || loadingUsers) return;

    const stillAvailable = usersFromDraftQueues.some(
      (userItem) => String(userItem.id) === String(draftAssignedUserId)
    );

    if (!stillAvailable) {
      setDraftAssignedUserId("");
    }
  }, [filtersModalOpen, draftQueueIds, draftAssignedUserId, usersFromDraftQueues, usersHasMore, loadingUsers]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setTab("open");
      return;
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    setTabOpen(newValue);
  };

  const handleOpenFiltersModal = () => {
    setDraftQueueIds(selectedQueueIds);
    setDraftTagId(selectedTagId);
    setDraftAssignedUserId(selectedAssignedUserId);
    setDraftDateFrom(selectedDateFrom);
    setDraftDateTo(selectedDateTo);
    setDraftWhatsappId(selectedWhatsappId);
    setDraftTransferred(selectedTransferred);
    setDraftUnreadOnly(selectedUnreadOnly);
    setFiltersModalOpen(true);
  };

  const handleCloseFiltersModal = () => {
    setFiltersModalOpen(false);
  };

  const handleClearFilters = () => {
    setDraftQueueIds(userQueueIds || []);
    setDraftTagId("");
    setDraftAssignedUserId("");
    setDraftDateFrom("");
    setDraftDateTo("");
    setDraftWhatsappId("");
    setDraftTransferred("");
    setDraftUnreadOnly(false);
  };

  const handleApplyFilters = () => {
    setSelectedQueueIds(draftQueueIds);
    setSelectedTagId(draftTagId);
    setSelectedAssignedUserId(draftAssignedUserId);
    setSelectedDateFrom(draftDateFrom);
    setSelectedDateTo(draftDateTo);
    setSelectedWhatsappId(draftWhatsappId);
    setSelectedTransferred(draftTransferred);
    setSelectedUnreadOnly(draftUnreadOnly);
    setFiltersModalOpen(false);
  };

  const handleClearAppliedFilters = () => {
    setSelectedQueueIds(userQueueIds || []);
    setSelectedTagId("");
    setSelectedAssignedUserId("");
    setSelectedDateFrom("");
    setSelectedDateTo("");
    setSelectedWhatsappId("");
    setSelectedTransferred("");
    setSelectedUnreadOnly(false);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(e) => setNewTicketModalOpen(false)}
        selectedWhatsappId={selectedWhatsappId}
      />
      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          aria-label="icon label tabs example"
        >
          <Tab
            value={"open"}
            icon={<Chat />}
            label={i18n.t("tickets.tabs.open.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"closed"}
            icon={<DoneAll />}
            label={i18n.t("tickets.tabs.closed.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value={"search"}
            icon={<SearchIcon />}
            label={i18n.t("tickets.tabs.search.title")}
            classes={{ root: classes.tab }}
          />
        </Tabs>
      </Paper>

      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        <div className={classes.serachInputWrapper}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            inputRef={searchInputRef}
            placeholder={i18n.t("tickets.search.placeholder")}
            type="search"
            onChange={handleSearch}
          />
        </div>

        <Button
          variant="text"
          onClick={() => setNewTicketModalOpen(true)}
          className={`${classes.colorIcon} ${classes.optionButton}`}
        >
          {i18n.t("ticketsManager.buttons.newTicket")}
        </Button>

        <Button
          variant="outlined"
          color="primary"
          onClick={handleOpenFiltersModal}
          startIcon={<FilterListIcon />}
          className={classes.optionButton}
        >
          Filtros{activeFilterCount ? ` (${activeFilterCount})` : ""}
        </Button>
      </Paper>

      {activeFilterCount > 0 && (
        <div className={classes.filterChips}>
          {queueFilterActive && (
            <Chip
              size="small"
              label={`Departamentos: ${selectedQueueIds.length || "todos"}`}
              onDelete={() => setSelectedQueueIds(userQueueIds || [])}
            />
          )}
          {selectedTagId && (
            <Chip
              size="small"
              label={`Etiqueta: ${selectedTag?.name || selectedTagId}`}
              onDelete={() => setSelectedTagId("")}
            />
          )}
          {selectedAssignedUserId && (
            <Chip
              size="small"
              label={
                selectedAssignedUserId === "unassigned"
                  ? "Atendente: Não atribuído"
                  : `Atendente: ${selectedAssignedUser?.name || selectedAssignedUserId}`
              }
              onDelete={() => setSelectedAssignedUserId("")}
            />
          )}
          {selectedDateFrom && (
            <Chip
              size="small"
              label={`De: ${selectedDateFrom}`}
              onDelete={() => setSelectedDateFrom("")}
            />
          )}
          {selectedDateTo && (
            <Chip
              size="small"
              label={`Ate: ${selectedDateTo}`}
              onDelete={() => setSelectedDateTo("")}
            />
          )}
          {selectedWhatsappId && (
            <Chip
              size="small"
              label={`Conexao: ${selectedWhatsapp?.name || selectedWhatsappId}`}
              onDelete={() => setSelectedWhatsappId("")}
            />
          )}
          {selectedTransferred && (
            <Chip
              size="small"
              label={selectedTransferred === "true" ? "Transferidos" : "Nao transferidos"}
              onDelete={() => setSelectedTransferred("")}
            />
          )}
          {selectedUnreadOnly && (
            <Chip
              size="small"
              label="Somente nao lidas"
              onDelete={() => setSelectedUnreadOnly(false)}
            />
          )}
          <Chip size="small" label="Limpar filtros" onDelete={handleClearAppliedFilters} />
        </div>
      )}

      <Dialog
        open={filtersModalOpen}
        onClose={handleCloseFiltersModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Filtros avançados</DialogTitle>
        <DialogContent dividers>
          <TicketsQueueSelect
            selectedQueueIds={draftQueueIds}
            userQueues={user?.queues}
            onChange={(values) => setDraftQueueIds(values)}
          />

          <FormControl
            size="small"
            variant="outlined"
            className={classes.filterControl}
            fullWidth
            margin="dense"
          >
            <InputLabel id="tag-filter-label">Etiqueta</InputLabel>
            <Select
              labelId="tag-filter-label"
              value={draftTagId}
              onChange={(e) => setDraftTagId(e.target.value)}
              label="Etiqueta"
            >
              <MenuItem value="">
                <em>Todas</em>
              </MenuItem>
              {tags.map((tag) => (
                <MenuItem key={tag.id} value={tag.id} disabled={tag.isGroup}>
                  {tag.isGroup ? tag.name : `${tag.parent?.name ? `${tag.parent.name} / ` : ""}${tag.name}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            variant="outlined"
            className={classes.filterControl}
            fullWidth
            margin="dense"
          >
            <InputLabel id="assigned-user-filter-label">Atendente</InputLabel>
            <Select
              labelId="assigned-user-filter-label"
              value={draftAssignedUserId}
              onChange={(e) => setDraftAssignedUserId(e.target.value)}
              label="Atendente"
              MenuProps={usersMenuProps}
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              <MenuItem value="unassigned">
                <em>Não atribuído</em>
              </MenuItem>
              {usersFromDraftQueues.map((userItem) => (
                <MenuItem key={userItem.id} value={userItem.id}>
                  {userItem.name}
                </MenuItem>
              ))}
              {loadingUsers && (
                <MenuItem disabled>
                  <CircularProgress size={18} />
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <TextField
            label="Data inicial"
            type="date"
            value={draftDateFrom}
            onChange={(e) => setDraftDateFrom(e.target.value)}
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Data final"
            type="date"
            value={draftDateTo}
            onChange={(e) => setDraftDateTo(e.target.value)}
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />

          {!loadingWhatsapps && (
            <FormControl
              size="small"
              variant="outlined"
              className={classes.filterControl}
              fullWidth
              margin="dense"
            >
              <InputLabel id="whatsapp-filter-label">Conexao</InputLabel>
              <Select
                labelId="whatsapp-filter-label"
                value={draftWhatsappId}
                onChange={(e) => setDraftWhatsappId(e.target.value)}
                label="Conexao"
              >
                <MenuItem value="">
                  <em>Todas</em>
                </MenuItem>
                {whatsApps.map((whatsapp) => (
                  <MenuItem key={whatsapp.id} value={whatsapp.id}>
                    {whatsapp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl
            size="small"
            variant="outlined"
            className={classes.filterControl}
            fullWidth
            margin="dense"
          >
            <InputLabel id="transferred-filter-label">Transferencia</InputLabel>
            <Select
              labelId="transferred-filter-label"
              value={draftTransferred}
              onChange={(e) => setDraftTransferred(e.target.value)}
              label="Transferencia"
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              <MenuItem value="true">Transferidos</MenuItem>
              <MenuItem value="false">Nao transferidos</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={draftUnreadOnly}
                onChange={(e) => setDraftUnreadOnly(e.target.checked)}
                color="primary"
              />
            }
            label="Somente conversas não lidas"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClearFilters}>Limpar</Button>
          <Button onClick={handleCloseFiltersModal}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleApplyFilters}>
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <Tabs
          value={tabOpen}
          onChange={handleChangeTabOpen}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab
            label={
              <Badge
                className={classes.openTabBadge}
                badgeContent={mineCount}
                color="primary"
                overlap="rectangular"
              >
                <span className={classes.openTabLabel}>
                  {i18n.t("ticketsList.myTicketsHeader")}
                </span>
              </Badge>
            }
            value={"mine"}
            classes={{ root: classes.openTab }}
          />
          <Tab
            label={
              <Badge
                className={classes.openTabBadge}
                badgeContent={openCount}
                color="primary"
                overlap="rectangular"
              >
                <span className={classes.openTabLabel}>
                  {i18n.t("ticketsList.assignedHeader")}
                </span>
              </Badge>
            }
            value={"open"}
            classes={{ root: classes.openTab }}
          />
          <Tab
            label={
              <Badge
                className={classes.openTabBadge}
                badgeContent={pendingCount}
                color="secondary"
                overlap="rectangular"
              >
                <span className={classes.openTabLabel}>
                  {i18n.t("ticketsList.pendingHeader")}
                </span>
              </Badge>
            }
            value={"pending"}
            classes={{ root: classes.openTab }}
          />
        </Tabs>
        <Paper className={classes.ticketsWrapper}>
          <TicketsList
            status="open"
            onlyMine={true}
            selectedQueueIds={selectedQueueIds}
            selectedTagId={selectedTagId}
            selectedAssignedUserId={selectedAssignedUserId}
            selectedDateFrom={selectedDateFrom}
            selectedDateTo={selectedDateTo}
            selectedWhatsappId={selectedWhatsappId}
            selectedTransferred={selectedTransferred}
            selectedUnreadOnly={selectedUnreadOnly}
            updateCount={(val) => setMineCount(val)}
            style={applyPanelStyle("mine")}
          />
          <TicketsList
            status="open"
            showAll={showAllTickets}
            selectedQueueIds={selectedQueueIds}
            selectedTagId={selectedTagId}
            selectedAssignedUserId={selectedAssignedUserId}
            selectedDateFrom={selectedDateFrom}
            selectedDateTo={selectedDateTo}
            selectedWhatsappId={selectedWhatsappId}
            selectedTransferred={selectedTransferred}
            selectedUnreadOnly={selectedUnreadOnly}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
          />
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            selectedTagId={selectedTagId}
            selectedAssignedUserId={selectedAssignedUserId}
            selectedDateFrom={selectedDateFrom}
            selectedDateTo={selectedDateTo}
            selectedWhatsappId={selectedWhatsappId}
            selectedTransferred={selectedTransferred}
            selectedUnreadOnly={selectedUnreadOnly}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
          />
        </Paper>
      </TabPanel>

      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={canShowAllTickets}
          selectedQueueIds={selectedQueueIds}
          selectedTagId={selectedTagId}
          selectedAssignedUserId={selectedAssignedUserId}
          selectedDateFrom={selectedDateFrom}
          selectedDateTo={selectedDateTo}
          selectedWhatsappId={selectedWhatsappId}
          selectedTransferred={selectedTransferred}
          selectedUnreadOnly={selectedUnreadOnly}
        />
      </TabPanel>

      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={canShowAllTickets}
          selectedQueueIds={selectedQueueIds}
          selectedTagId={selectedTagId}
          selectedAssignedUserId={selectedAssignedUserId}
          selectedDateFrom={selectedDateFrom}
          selectedDateTo={selectedDateTo}
          selectedWhatsappId={selectedWhatsappId}
          selectedTransferred={selectedTransferred}
          selectedUnreadOnly={selectedUnreadOnly}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManager;
